import type { SyntheticEvent } from 'react';
import { getNumbersFromStr } from '@alfa-bank/site-form-helpers/text/get-numbers-from-str';
import { isEditableIndex } from '@alfa-bank/site-input/src/helpers';

import { MASK_TYPES, FORMAT_CHARS } from './config';
import type { TMaskOptions, TPhoneType } from './models';

/**
 * Группирует повторяющиеся символы в маске.
 * Пример: +7 (999) 999-99-99 —> [["+"], ["7"], [" "], ["("], ["9", "9", "9"], [")"], [" "], ["9", "9", "9"], ["-"], ["9", "9"], ["-"], ["9", "9"]]
 * @param mask — маска
 */

interface IFormatChar {
    [key: string]: string;
}
const groupSameCharactersFromMask = (mask: string) => {
    const maskChars = Array.from(mask);

    return maskChars.reduce((groups, char: string, i) => {
        if (i < 1 || mask[i - 1] !== char) {
            return [...groups, [char]];
        }

        const oldGroup = groups[groups.length - 1];

        return [...groups.slice(0, -1), [...oldGroup, char]];
    }, [] as string[][]);
};

/**
 * Отдает регулярное выражение соотв. переданной маске.
 * Пример: +7 (999) 999-99-99 —> /(\d{3})(\d{3})(\d{2})(\d{2})/
 * @param mask - маска
 * @param formatChars — словарь с сопоставлением «символ -> регулярное выражение»
 */
export const getRegexpFromMask = (mask = '', formatChars: IFormatChar = {}) => {
    return new RegExp(
        groupSameCharactersFromMask(mask)
            .filter(group => formatChars[group[0]])
            .reduce((regexp, group) => `${regexp}(${formatChars[group[0]]}{${group.length}})`, '')
    );
};

/**
 * Отдает замещающую строку соотв. переданной маске.
 * Пример: +7 (999) 999-99-99 —> +7 ($1) $2-$3-$4
 * @param mask - маска
 * @param formatChars — словарь с сопоставлением «символ -> регулярное выражение»
 */
export const getReplacerFromMask = (mask = '', formatChars: IFormatChar = {}) => {
    let lastIndex = 0;

    return groupSameCharactersFromMask(mask).reduce((acc, group) => {
        if (formatChars[group[0]]) {
            lastIndex += 1;

            return `${acc}$${lastIndex}`;
        }

        return `${acc}${group[0]}`;
    }, '');
};

/**
 * Проверяет валидность кода оператора
 * @param value — массив цифр
 */
export const hasInvalidOperatorCode = (value: string[]) =>
    (value[1] === '8' && ['8', '9'].includes(value[2])) ||
    (value[1] === '7' && ['7', '9'].includes(value[2]));

/**
 * Функция используется для проверки корректности отображения значения телефона в инпуте с использование маски.
 * Функция,  оставить только цифры, и символы использующиеся в маске "+7 abc(123)" -> "+7 (123)",
 * обрежет двойные пробелы '+7  (999)  999- 99-99'  -> '+7 (999) 999-99-99'
 * @param value строка с номером телефона
 */
export const getCorrectDisplayedPhone = (value: string) =>
    value
        .trim()
        .replace(/[^\d\s()+-]+/g, '')
        .replace(/\s+/g, ' ');

export const closestEditableIndex = (finalIdx: number, currIndex: number, permanents: number[]) => {
    if (currIndex < finalIdx) return 0;

    let index = currIndex;

    while (index > finalIdx && permanents.includes(index)) {
        index -= 1;
    }

    return index;
};

export const parseMask = (type: 'tel' | 'phone') => {
    const permanents: number[] = [];
    const mask = MASK_TYPES[type];

    let parsedMaskString = '';

    let prefix = '';

    let lastEditablePosition = null;

    let isPermanent = false;

    mask.split('').forEach(character => {
        if (isPermanent || !(character in FORMAT_CHARS)) {
            permanents.push(parsedMaskString.length);
            if (parsedMaskString.length === permanents.length - 1) {
                prefix += character;
            }
        } else {
            lastEditablePosition = parsedMaskString.length + 1;
        }
        parsedMaskString += character;
        isPermanent = false;
    });

    return {
        prefix,
        mask: parsedMaskString,
        lastEditablePosition,
        permanents,
    };
};

export const setInputSelection = (inputElement: HTMLInputElement, selection: number) => {
    // eslint-disable-next-line no-param-reassign
    inputElement.selectionStart = selection;
    // eslint-disable-next-line no-param-reassign
    inputElement.selectionEnd = selection;
};

export const getFormatFunction =
    (maskOptions: TMaskOptions, type: TPhoneType) => (value: string) => {
        const pureValue = getNumbersFromStr(value).split('');
        const pureMask = getNumbersFromStr(maskOptions.mask);

        let formattedValue = '';

        // Позиция, с которой пользователь начинает вводить номер равна длине префикса
        let valueIndex = getNumbersFromStr(maskOptions.prefix).length;

        let patternIndex = 0;

        /*
         * Если поле заполнено и первые две цифры кода оператора равны '88' или '89',
         * «сдвигаем» номер телефона влево
         */
        if (
            type === 'tel' &&
            pureValue.length > pureMask.length &&
            hasInvalidOperatorCode(pureValue)
        ) {
            const regExp = getRegexpFromMask(maskOptions.mask, FORMAT_CHARS);
            const replacer = getReplacerFromMask(maskOptions.mask, FORMAT_CHARS);

            return (
                pureValue
                    // Убираем префикс плюс лишнюю восьмерку
                    .slice(valueIndex + 1, pureMask.length + 1)
                    .join('')
                    .replace(regExp, replacer)
            );
        }

        if (value.length < maskOptions.prefix.length || pureValue.length < 2)
            return maskOptions.prefix;

        // проходимся по каждому символу в маске
        while (
            patternIndex < Number(maskOptions.lastEditablePosition) &&
            valueIndex < pureValue.length
        ) {
            /*
             * если текущий символ из маски нередактируемый,
             * то добавляем этот символ в отформатированное значение и переходим на следующий символ
             */
            if (isEditableIndex(patternIndex, maskOptions.permanents) === false) {
                const patternChar = maskOptions.mask[patternIndex];

                formattedValue += patternChar;
                patternIndex += 1;
                // если текущий символ редактируемый
            } else {
                formattedValue += pureValue[valueIndex];
                valueIndex += 1;
                patternIndex += 1;
            }
        }

        return formattedValue;
    };

export const correctCarriagePosition = (
    event: SyntheticEvent<HTMLInputElement>,
    prefixLength: number
) => {
    const { selectionStart, setSelectionRange } = event.target as HTMLInputElement;

    const carriagePositionOnPrefix = Number(selectionStart) < prefixLength;

    // корректировать положение каретки, если пользователь устанавливает ее до "("
    if (carriagePositionOnPrefix) {
        setSelectionRange.call(event.target, prefixLength, prefixLength);
    }
};
